/**
 * Reading an Enrich Contacts node's config the same way the config panel does.
 *
 * The provider list has three historical shapes and the node card has to agree
 * with EnrichContactsConfig on all of them, or the summary on the canvas
 * contradicts the panel that wrote it:
 *
 *   - `undefined` — the config predates the `providers` field (a brand-new
 *     node, or a workflow saved before multi-provider support). Both the panel
 *     and the backend (workflow_enrichment.py) treat that as "run native", so
 *     it counts as one provider.
 *   - `[]` — the user deliberately unchecked every provider. Genuinely zero;
 *     not the same thing as `undefined`.
 *   - entries that are plain strings rather than `{name}` objects — the older
 *     serialisation, still present in saved workflows.
 *
 * Enablement is PRESENCE in the array, not an `enabled` flag: the panel's
 * toggle adds and removes entries (`isProviderEnabled` only checks the name).
 * An entry carrying `enabled: false` is not something the panel can produce.
 */

/**
 * Every block the Native (AI) provider can run, and the ONE list both the
 * config panel and the node card count against.
 *
 * They used to disagree. The panel offered eleven and its "All" button
 * selected eleven; the card divided by a hardcoded 10, so "All" rendered
 * "11/10 blocks". The 10 came from the backend's ALL_ENRICHMENT_BLOCKS, which
 * is a different set on purpose: `custom_ai` is stripped before the graph runs
 * and executed afterwards as post-processing (native_adapter), so it is not
 * one of the graph's filterable blocks. It is still a real thing the user
 * ticks, so it belongs in the count they see.
 *
 * Exported so the panel imports it rather than keeping a second copy. A
 * denominator that cannot drift from the list is the point.
 */
export const ALL_ENRICHMENT_BLOCKS = [
  'person', 'company', 'sector', 'economy',
  'outreach_hooks', 'buying_signals', 'communication_prefs',
  'competitive_intel', 'content_relevance',
  'relationship_map',
  'custom_ai',
] as const

export const ALL_ENRICHMENT_BLOCKS_COUNT = ALL_ENRICHMENT_BLOCKS.length

/**
 * The block kept when the user clears the selection.
 *
 * Native with nothing ticked is the one setting that can only waste money: the
 * graph skips the LLM entirely and returns empty blocks, while the run still
 * costs its credits. relationship_map is the right survivor because it is the
 * only block that needs no model call at all.
 */
export const FALLBACK_ENRICHMENT_BLOCK = 'relationship_map'

/** Shown when an action would leave no block selected. */
export const NO_BLOCKS_MESSAGE =
  'Keep at least one block. Native with none selected costs credits and returns nothing.'

type ProviderEntry = string | { name?: string } | null | undefined

export interface EnrichContactsConfigLike {
  providers?: ProviderEntry[]
  enabled_blocks?: unknown[]
  skip_already_enriched?: boolean
  max_age_days?: number
}

/** Names of the providers this node will run, in config order. */
export function enabledProviderNames(config?: EnrichContactsConfigLike | null): string[] {
  const providers = config?.providers
  // Missing (not empty) means a pre-multi-provider config: native by default.
  if (providers === undefined || providers === null) return ['native']
  if (!Array.isArray(providers)) return []
  return providers
    .map(p => (typeof p === 'string' ? p : p?.name))
    .filter((name): name is string => typeof name === 'string' && name.length > 0)
}

export function enabledProviderCount(config?: EnrichContactsConfigLike | null): number {
  return enabledProviderNames(config).length
}

export function isNativeEnrichmentEnabled(config?: EnrichContactsConfigLike | null): boolean {
  return enabledProviderNames(config).includes('native')
}

/**
 * The one-line summary under the node's title.
 *
 * Provider count leads, because that is what the card was missing: it used to
 * show only a block count, so selecting or deselecting a provider changed
 * nothing on the canvas (AB#21978).
 *
 * The block count is native-only — `enabled_blocks` drives the Native (AI)
 * provider and nothing else — so it is omitted when native is off rather than
 * quoting a number that does not apply to the providers actually running.
 */
export function enrichmentNodeSummary(config?: EnrichContactsConfigLike | null): string {
  const count = enabledProviderCount(config)
  const parts: string[] = [
    count === 0 ? 'No providers' : `${count} provider${count === 1 ? '' : 's'}`,
  ]

  if (isNativeEnrichmentEnabled(config)) {
    const blocks = Array.isArray(config?.enabled_blocks)
      ? config!.enabled_blocks!.length
      : ALL_ENRICHMENT_BLOCKS_COUNT
    parts.push(`${blocks}/${ALL_ENRICHMENT_BLOCKS_COUNT} blocks`)
  }

  if (config?.skip_already_enriched !== false) parts.push('Skip enriched')
  const maxAge = config?.max_age_days || 0
  if (maxAge > 0) parts.push(`${maxAge}d refresh`)

  return parts.join(' · ')
}

/**
 * Enrichment providers parked for a future scope, mirrored from the backend's
 * orchestrator.PROVIDER_METADATA.
 *
 * The server already stops returning these, so this is belt and braces rather
 * than the fix. It earns its place because the two halves deploy separately:
 * the frontend is a Static Web App and the backend is a container, so a
 * frontend carrying this change can talk to a backend that predates it and the
 * five reappear, each row disabled and telling the user to "Configure in
 * Settings" on a screen that does not offer them.
 *
 * Keyed by provider name, so when the Settings fields land, deleting a name
 * here and uncommenting its backend block is the whole job. Deliberately NOT a
 * general "hide anything unavailable" rule: Apollo with no key yet is also
 * unavailable, and must still be shown so the user can go and configure it.
 */
export const PARKED_ENRICHMENT_PROVIDERS = new Set([
  'rocketreach', 'lusha', 'cognism', 'uplead', 'snov',
])

export function offerableProviders<T extends { name?: string }>(providers: T[] | null | undefined): T[] {
  if (!Array.isArray(providers)) return []
  return providers.filter(p => !PARKED_ENRICHMENT_PROVIDERS.has((p?.name || '').trim().toLowerCase()))
}
